import { useCallback, useMemo } from 'react';
import { createBetPayload } from '@root/lib';
import { useStomp } from '@root/providers';
import { betValuesSelector } from '@root/store/slices/betValuesSlice';
import {
  fundingSelector,
  isRunClosedByFunding,
} from '@root/store/slices/fundingSlice';
import { journeySelector } from '@root/store/slices/journeySlice';
import { marketSelector } from '@root/store/slices/marketSlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import { useActions, useTypedSelector } from '@root/store/store';
import {
  MarketStatus,
  type Market,
  type SelectionWithMarket,
} from '@root/store/types';
import { submitBet } from '@shared/services/betSlip';

// Ludo has one market with one selection. Rolling is just backing it.
const ROLL_SELECTION = 'Roll';

export function useRoll() {
  const { emitMessage } = useStomp();
  const { round } = useTypedSelector(roundSelector);
  const { markets } = useTypedSelector(marketSelector);
  const { rollInFlight, rollSubmitted } = useTypedSelector(journeySelector);
  const actions = useActions();
  const { status: fundingStatus, shortBalance } =
    useTypedSelector(fundingSelector);
  const closedByFunding = useTypedSelector(isRunClosedByFunding);

  const { currentBetAmount } = useTypedSelector(betValuesSelector);

  const state = round?.state;
  const isFree = state?.nextRollFree ?? false;
  // The first roll locks the stake, so a stake means the run has started.
  const hasRun = (state?.rollAmount ?? 0) > 0;
  // Until a run locks it, the amount is whatever the player has picked.
  const rollAmount = hasRun ? (state?.rollAmount ?? 0) : currentBetAmount;

  const selection = useMemo<SelectionWithMarket | null>(() => {
    const market: Market | undefined = markets[markets.length - 1];
    const found = market?.selections?.find(
      (s) => s.name === ROLL_SELECTION || s.reference === ROLL_SELECTION
    );
    if (!market || !found || !round) return null;
    return {
      ...found,
      marketId: market.id,
      roundId: round.id,
      gameId: market.gameId,
      marketReference: market.reference,
      marketStatus: market.status,
      market,
    };
  }, [markets, round]);

  // Only a finished run blocks. journeyStatus is null until the first roll.
  const journeyOver =
    state?.journeyStatus === 'WON' || state?.journeyStatus === 'ELIMINATED';

  const canRoll =
    !!selection &&
    !rollInFlight &&
    !rollSubmitted &&
    !shortBalance &&
    fundingStatus !== 'NEEDS_FUNDING' &&
    !closedByFunding &&
    !journeyOver &&
    selection.marketStatus === MarketStatus.OPEN;

  const roll = useCallback(async () => {
    if (!selection || rollSubmitted) return false;
    // A new clientBetId per call, so two clicks are two bets. Lock on click.
    actions.setRollSubmitted(true);
    // A free roll is a real bet at zero stake, not a skipped one.
    const amount = isFree ? 0 : rollAmount;
    const { sent } = await submitBet(
      emitMessage,
      createBetPayload(selection, amount)
    );
    if (!sent) actions.setRollSubmitted(false);
    return sent;
  }, [actions, emitMessage, isFree, rollAmount, rollSubmitted, selection]);

  return { roll, canRoll, isFree, rollAmount, hasRun };
}
